import { useAppState } from '@/ui/AppState';
import { CountChip, EmptyState } from './bits';
import type { SessionKind } from '@/domain/types';

const EMPTY: Record<SessionKind, { title: string; body: string }> = {
  class: {
    title: 'No classes yet',
    body: 'Import a class from the Add class screen first.',
  },
  chapter: {
    title: 'No chapter reviews yet',
    body: 'Chapter reviews use kind "chapter" and an id like ch-3 in the import block.',
  },
  textbook: {
    title: 'No textbook chapters yet',
    body: 'Textbook chapters use kind "textbook" and an id like tb-3 in the import block.',
  },
};

export function SessionPicker({
  kind,
  want,
  onPick,
}: {
  kind: SessionKind;
  want: 'flashcard' | 'question';
  onPick: (sessionId: string) => void;
}) {
  const { sessions, items } = useAppState();
  const matching = sessions.filter((s) => s.kind === kind);

  const countFor = (sessionId: string) =>
    items.filter(
      (i) =>
        i.sessionId === sessionId &&
        (want === 'flashcard' ? i.type === 'flashcard' : i.type !== 'flashcard'),
    ).length;

  if (matching.length === 0) {
    return (
      <EmptyState icon="📄" title={EMPTY[kind].title}>
        {EMPTY[kind].body}
      </EmptyState>
    );
  }

  return (
    <ul className="list">
      {matching.map((session) => {
        const count = countFor(session.id);
        return (
          <li key={session.id}>
            <button className="list-item" disabled={count === 0} onClick={() => onPick(session.id)}>
              <div className="list-item__main">
                <div className="list-item__title">{session.title}</div>
                <div className="list-item__sub">{session.shortLabel}</div>
              </div>
              <CountChip>
                {count} {want === 'flashcard' ? 'cards' : 'questions'}
              </CountChip>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
